import { EnumSearchType, EnumTrendType } from '@domains/research/shared';

import { IResearchFilter, IResearchWorkflowOptions, ResearchWorkflowPage } from './research-workflow-page';


interface IOption { value: string; label?: string; disabled?: boolean }
interface IWorkflowFactoryOptions extends Omit<IResearchWorkflowOptions, 'getFilterDefinitions' | 'validateFilters'> {
	kind: 'best-sellers' | 'seller-trends' | 'real-time-research';
	choices(): { marketplaces: IOption[]; products: IOption[]; sorting: IOption[]; timePeriods?: IOption[] };
	advancedAllowed(): boolean;
	validateMarketProduct(filters: Record<string, unknown>): void;
}

export function createResearchWorkflow(options: IWorkflowFactoryOptions): ResearchWorkflowPage {
	const definitions = (): Record<string, IResearchFilter> => {
		const choices = options.choices();
		const select = (items: IOption[], includeDisabled = false): IResearchFilter => ({ type: 'string', values: items.filter((o) => includeDisabled || !o.disabled).map((o) => ({ value: o.value, label: o.label || o.value })) });
		const values = (items: string[]): IResearchFilter => select(items.map((value) => ({ value })));
		const boolean: IResearchFilter = { type: 'boolean' };
		const rank: IResearchFilter = { type: 'number', min: 1, max: options.kind === 'seller-trends' ? 2000000 : 5000000 };
		// Dropdown compatibility reflects the old pair. Validate the complete proposed
		// pair (including maintenance restrictions) before applying either field.
		const filters: Record<string, IResearchFilter> = { marketplace: select(choices.marketplaces, true), product: select(choices.products, true), [options.kind === 'real-time-research' ? 'sorting' : 'sort_by']: select(choices.sorting) };
		if (options.kind === 'real-time-research') return { ...filters, excludeBrands: boolean };
		if (!options.advancedAllowed()) return filters;
		Object.assign(filters, { search_type: values(Object.values(EnumSearchType)), title_only: boolean, from_bsr: rank, to_bsr: rank });
		if (options.kind === 'seller-trends') return { ...filters, bsr_tp: select(choices.timePeriods || []), trend_type: values(Object.values(EnumTrendType)) };
		return { ...filters, enablePriceFilter: boolean, enableBsrFilter: boolean, exclude_ob: boolean, from_price: { type: 'number', min: 0, max: 50 }, to_price: { type: 'number', min: 0, max: 50 }, rating: { type: 'number', min: 0, max: 5 }, excluded_texts: { type: 'list' }, from_p_date: { type: 'string' }, to_p_date: { type: 'string' } };
	};
	return new ResearchWorkflowPage({ ...options, getFilterDefinitions: definitions, validateFilters: (filters) => {
		options.validateMarketProduct(filters);
		for (const [from, to] of [['from_price', 'to_price'], ['from_bsr', 'to_bsr'], ['from_p_date', 'to_p_date']]) {
			if (filters[from] !== null && filters[to] !== null && filters[from] !== undefined && filters[to] !== undefined && filters[from] > filters[to]) throw new Error(`${from} must not exceed ${to}.`);
		}
		for (const key of ['from_p_date', 'to_p_date']) {
			const value = filters[key];
			if (value && (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(Date.parse(value)) || new Date(value).toISOString().slice(0, 10) !== value)) throw new Error(`Invalid date for ${key}. Use YYYY-MM-DD.`);
		}
	} });
}
